import Button from 'components/Button';
import LinkButton from 'components/LinkButton';
import TooltipWrapper from 'components/Tooltip';
import {FC} from 'react';
import {useFormContext} from 'react-hook-form';
import {t} from 'ttag';
import {setUrl} from 'utils/request';
import {onApiError} from 'utils/request-axios/utils';

import {EmployeeActions} from '../types';
import {useMemberOverviewDelete, useMemberOverviewResend} from './services';

type EmployeeActionButtonsProps = {
    mid: string;
} & EmployeeActions;

const EmployeeActionButtons: FC<EmployeeActionButtonsProps> = ({
    canDeleteEmployee,
    canEditEmployee,
    canResendInvitation,
    canRevokeEmployee,
    mid,
}) => {
    const methods = useFormContext();

    const {mutateAsync: mutateAsyncResend} = useMemberOverviewResend();
    const {mutateAsync: mutateAsyncDelete} = useMemberOverviewDelete();

    const onResend = async () => {
        mutateAsyncResend(mid)
            .then((response) => {
                if (response?.message) {
                    methods.setError('root.successMessage', {
                        message: response.message,
                        type: 'success',
                    });
                }
            })
            .catch(onApiError(methods));
    };

    const onDelete = async () => {
        mutateAsyncDelete(mid)
            .then(() => {
                setUrl(window.Urls.employee_list_view());
            })
            .catch(onApiError(methods));
    };

    return (
        <div className="mt-8 flex gap-2" id="employee-actions">
            {canEditEmployee && (
                <TooltipWrapper message={canEditEmployee.failureReason}>
                    <LinkButton
                        disabled={!canEditEmployee.allowed}
                        href={window.Urls.employee_edit_view(mid)}
                        icon="edit"
                        kind="secondary"
                        size="sm"
                    >
                        {t`Edit Team Member Info`}
                    </LinkButton>
                </TooltipWrapper>
            )}
            {canResendInvitation && (
                <TooltipWrapper message={canResendInvitation.failureReason}>
                    <Button
                        disabled={!canResendInvitation.allowed}
                        icon="send"
                        kind="secondary"
                        onClick={() => onResend()}
                        size="sm"
                    >
                        {t`Resend Invitation`}
                    </Button>
                </TooltipWrapper>
            )}
            {canRevokeEmployee && (
                <TooltipWrapper message={canRevokeEmployee.failureReason}>
                    <LinkButton
                        disabled={!canRevokeEmployee.allowed}
                        href={window.Urls.employee_revoke_view(mid)}
                        kind="destructive"
                        size="sm"
                    >
                        {t`Revoke Access`}
                    </LinkButton>
                </TooltipWrapper>
            )}
            {canDeleteEmployee && (
                <TooltipWrapper message={canDeleteEmployee.failureReason}>
                    <Button
                        aria-label={t`Delete`}
                        disabled={!canDeleteEmployee.allowed}
                        icon="trash"
                        kind="destructive"
                        onClick={() => onDelete()}
                        size="sm"
                    />
                </TooltipWrapper>
            )}
        </div>
    );
};

export default EmployeeActionButtons;
